/* ------------------------------------------------------------------ */
/*  Runtime DOM translation engine (sv -> en)                          */
/*                                                                     */
/*  The app's UI copy is authored in Swedish across many components.   */
/*  Rather than rewrite every component, this engine swaps visible     */
/*  Swedish text for English at runtime using a curated 1:1 dictionary */
/*  and restores Swedish when toggled back.                            */
/*                                                                     */
/*  Design goals:                                                      */
/*   - Zero changes to existing component logic.                       */
/*   - Idempotent, reversible, loop-safe even though React reuses and  */
/*     rewrites text nodes between renders.                            */
/*   - Safe: never touches script/style/textarea/editable regions.    */
/*   - Cheap: exact-match lookup on normalized text; a MutationObserver */
/*     keeps late-rendered content translated.                         */
/* ------------------------------------------------------------------ */

import enDict from './dictionary.en.json'

export type AppLanguage = 'sv' | 'en'

const STORAGE_KEY = 'hjalptorget:lang'

const DICTIONARIES: Record<Exclude<AppLanguage, 'sv'>, Record<string, string>> = {
  en: enDict as Record<string, string>,
}

// Attributes worth translating (placeholders, titles, aria labels, alt text).
const ATTRS = ['placeholder', 'title', 'aria-label', 'alt'] as const
type Attr = (typeof ATTRS)[number]

// Tags whose text content must never be translated.
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA'])

interface TextRecord {
  src: string
  out: string
}
const textState = new WeakMap<Text, TextRecord>()

type AttrRecord = Partial<Record<Attr, { src: string; out: string }>>
const attrState = new WeakMap<Element, AttrRecord>()

let current: AppLanguage = 'sv'
let observer: MutationObserver | null = null
let scheduled = false

function norm(s: string): string {
  return s.replace(/\s+/g, ' ').trim()
}

function lookup(raw: string, dict: Record<string, string>): string | null {
  const key = norm(raw)
  if (key.length < 1) return null
  const hit = dict[key]
  if (hit == null) return null
  const lead = raw.match(/^\s*/)?.[0] ?? ''
  const trail = raw.match(/\s*$/)?.[0] ?? ''
  return lead + hit + trail
}

function shouldSkip(parent: Element | null): boolean {
  if (!parent) return false
  if (SKIP_TAGS.has(parent.tagName)) return true
  if ((parent as HTMLElement).isContentEditable) return true
  return false
}

function translateTextNode(node: Text, dict: Record<string, string>): void {
  const parent = node.parentElement
  if (shouldSkip(parent)) return

  const value = node.nodeValue ?? ''
  const rec = textState.get(node)

  // If the current value is exactly what we last wrote, the source is the
  // stored Swedish original; otherwise React replaced it, so the current
  // value IS the new source.
  const src = rec && value === rec.out ? rec.src : value

  const out = lookup(src, dict)
  if (out == null) {
    if (rec) textState.delete(node)
    return
  }
  if (value !== out) node.nodeValue = out
  textState.set(node, { src, out })
}

function restoreTextNode(node: Text): void {
  const rec = textState.get(node)
  if (!rec) return
  if ((node.nodeValue ?? '') === rec.out) node.nodeValue = rec.src
  textState.delete(node)
}

function translateAttrs(el: Element, dict: Record<string, string>): void {
  let recs = attrState.get(el)
  for (const attr of ATTRS) {
    if (!el.hasAttribute(attr)) continue
    const value = el.getAttribute(attr) ?? ''
    const prev = recs?.[attr]
    const src = prev && value === prev.out ? prev.src : value
    const out = lookup(src, dict)
    if (out == null) {
      if (recs && recs[attr]) delete recs[attr]
      continue
    }
    if (value !== out) el.setAttribute(attr, out)
    if (!recs) recs = {}
    recs[attr] = { src, out }
  }
  if (recs) attrState.set(el, recs)
}

function restoreAttrs(el: Element): void {
  const recs = attrState.get(el)
  if (!recs) return
  for (const attr of ATTRS) {
    const rec = recs[attr]
    if (rec && (el.getAttribute(attr) ?? '') === rec.out) el.setAttribute(attr, rec.src)
  }
  attrState.delete(el)
}

function walk(root: Node, onText: (n: Text) => void, onEl: (el: Element) => void): void {
  if (root.nodeType === Node.ELEMENT_NODE) onEl(root as Element)
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode(n) {
      const parent = n.nodeType === Node.TEXT_NODE ? n.parentElement : (n as Element)
      if (parent && SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  let cur = tw.nextNode()
  while (cur) {
    if (cur.nodeType === Node.TEXT_NODE) onText(cur as Text)
    else onEl(cur as Element)
    cur = tw.nextNode()
  }
}

function applyTo(root: Node, lang: AppLanguage): void {
  if (lang === 'sv') {
    walk(
      root,
      (n) => restoreTextNode(n),
      (el) => restoreAttrs(el),
    )
    return
  }
  const dict = DICTIONARIES[lang]
  if (!dict) return
  walk(
    root,
    (n) => translateTextNode(n, dict),
    (el) => translateAttrs(el, dict),
  )
}

function scheduleFullPass(): void {
  if (scheduled) return
  scheduled = true
  requestAnimationFrame(() => {
    scheduled = false
    if (document.body) applyTo(document.body, current)
  })
}

function startObserver(): void {
  if (observer || typeof MutationObserver === 'undefined') return
  observer = new MutationObserver((mutations) => {
    if (current === 'sv') return
    const dict = DICTIONARIES[current]
    if (!dict) return
    for (const m of mutations) {
      if (m.type === 'childList') {
        m.addedNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) translateTextNode(node as Text, dict)
          else if (node.nodeType === Node.ELEMENT_NODE) applyTo(node, current)
        })
      } else if (m.type === 'characterData') {
        if (m.target.nodeType === Node.TEXT_NODE) translateTextNode(m.target as Text, dict)
      } else if (m.type === 'attributes' && m.target.nodeType === Node.ELEMENT_NODE) {
        translateAttrs(m.target as Element, dict)
      }
    }
  })
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTRS as unknown as string[],
  })
}

export function getStoredLanguage(): AppLanguage {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'sv'
  } catch {
    return 'sv'
  }
}

export function setDomLanguage(lang: AppLanguage): void {
  current = lang
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    /* storage unavailable, ignore */
  }
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = lang
  }
  if (typeof document !== 'undefined' && document.body) applyTo(document.body, lang)
}

export function initDomTranslation(): void {
  if (typeof document === 'undefined') return
  current = getStoredLanguage()
  if (document.documentElement) document.documentElement.lang = current
  const boot = () => {
    startObserver()
    if (current !== 'sv') scheduleFullPass()
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true })
  } else {
    boot()
  }
}
